import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getLatestPuzzles } from '../services/api';
import { formatPuzzleType } from '../utils/formatters';
import Copper from '../assets/carousel/CopperRiver.jpg';
import Augusta1 from '../assets/carousel/Augusta1.png';
import Augusta2 from '../assets/carousel/Augusta2.png';
import tpc1 from '../assets/carousel/tpc1.jpg';
import tpc2 from '../assets/carousel/tpc2.jpg';

interface Puzzle {
  id: string;
  title: string;
  description: string;
  price: number;
  photos: string[];
  puzzleType: string;
  availability: string;
}

const originalImages = [Augusta1, tpc1, Augusta2, Copper, tpc2];

const duplicatedImages = [
  originalImages[originalImages.length - 1],
  ...originalImages,
  originalImages[0],
];

const Home: React.FC = () => {
  const [latestPuzzles, setLatestPuzzles] = useState<Puzzle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [carouselIdx, setCarouselIdx] = useState(1); // Start at first real slide
  const [isTransitioning, setIsTransitioning] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null); // Type-safe ref

  useEffect(() => {
    const fetchLatestPuzzles = async () => {
      try {
        const data = await getLatestPuzzles(3);
        setLatestPuzzles(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load latest puzzles.');
        setLoading(false);
      }
    };

    fetchLatestPuzzles();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIdx((prev) => prev + 1);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Transition end handler for seamless looping
  useEffect(() => {
    const handleTransitionEnd = () => {
      if (carouselIdx === duplicatedImages.length - 1) {
        // At duplicate last slide, jump instantly to first real slide
        setIsTransitioning(false);
        setCarouselIdx(1);
      } else if (carouselIdx === 0) {
        // At duplicate first slide, jump instantly to last real slide
        setIsTransitioning(false);
        setCarouselIdx(duplicatedImages.length - 2);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('transitionend', handleTransitionEnd);
    }

    return () => {
      if (container) {
        container.removeEventListener('transitionend', handleTransitionEnd);
      }
    };
  }, [carouselIdx, duplicatedImages.length]);

  // Re-enable transition after instant jump
  useEffect(() => {
    if (!isTransitioning) {
      // In the next tick, re-enable the transition for smooth forward motion
      requestAnimationFrame(() => {
        setIsTransitioning(true);
      });
    }
  }, [isTransitioning]);

  return (
    <div className="bg-cream">
      {/* Hero Section */}
      <section className="flex flex-col items-center text-center py-12">
        <img src={require('../assets/logo.svg').default} alt="Logo" className="h-28 mb-6" />
        <h1 className="text-5xl font-bold mb-4">FirstCut Puzzles</h1>
        <p className="text-lg mb-6 max-w-xl mx-auto">
          Bring any course to your desktop
        </p>
        <Link to="/puzzles" className="bg-orange text-white px-8 py-3 rounded font-bold hover:bg-logo-gray transition-colors mb-8">Shop Now</Link>
      </section>

      {/* Large Image Carousel Section */}
      <section className="w-full overflow-hidden relative h-96">
        <div 
          ref={containerRef}
          style={{
            display: 'flex',
            width: `${duplicatedImages.length * 100}%`,
            transform: `translateX(-${carouselIdx * (100 / duplicatedImages.length)}%)`,
            transition: isTransitioning ? 'transform 0.7s cubic-bezier(0.4,0,0.2,1)' : 'none',
            height: '100%',
          }}>
          {duplicatedImages.map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt={`Carousel ${idx + 1}`}
              className="w-full h-96 object-cover flex-shrink-0"
              style={{ width: `${100 / duplicatedImages.length}%`, height: '100%', }}
            />
          ))}
        </div>
      </section>

      {/* Latest Puzzles Section */}
      <section className="py-16 px-4">
        <div className="text-orange font-bold mb-2">Latest Additions</div>
        <h2 className="text-3xl font-bold mb-10">Our newest puzzles:</h2>
        {loading ? (
          <div className="text-center">Loading latest puzzles...</div>
        ) : error ? (
          <div className="text-center text-red-500">{error}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {latestPuzzles.map((puzzle) => (
              <div key={puzzle.id} className="bg-white p-6 rounded shadow text-left">
                <Link to={`/puzzles/${puzzle.id}`}>
                  <div className="relative w-full pt-[75%] mb-4">
                    <img 
                      src={puzzle.photos[0]} 
                      alt={puzzle.title} 
                      className="absolute top-0 left-0 w-full h-full object-contain rounded" 
                    />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{puzzle.title}</h3>
                  <p className="text-gray-700 text-sm">
                    {formatPuzzleType(puzzle.puzzleType)}
                  </p>
                  <p className="text-orange font-bold mt-2">${puzzle.price.toFixed(2)}</p>
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Testimonial Section */}
      {<section className="py-16 text-center">
        <Link to="/puzzles" className="bg-orange text-white px-6 py-2 rounded font-bold hover:bg-logo-gray transition-colors">
          See All Courses
        </Link>
        {/* <div className="max-w-3xl mx-auto">
          <p className="text-2xl mb-4">I love FirstCut Puzzles because their leaders are progressive, and they always want to satisfy my needs.</p>
          <div className="text-lg">- Jane Smith</div>
        </div> */}
      </section>}

      {/* Call to Action Section */}

      <section className="py-16 text-center">
        <h2 className="text-3xl font-bold mb-6">
          Request your favorite course or hole!
        </h2>
        <Link to="/request" className="bg-orange text-white px-8 py-3 rounded font-bold hover:bg-logo-gray transition-colors">
          Suggest a Course
        </Link>
      </section>
    </div>
  );
};

export default Home; 